import { Check, Home, MessageCircle, MessageSquareMore, Send, UserRound, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { ChatbotConfig, ChatbotProps, Message } from "../types";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Card, CardAction, CardContent, CardFooter } from "./ui/card";


// ChatSessionManager class for handling session management
class ChatSessionManager {
  private socketUrl: string;
  private socket: Socket | null = null;
  private sessionId: string | null = null;
  private chatId: string | null = null;
  private isInitialized: boolean = false;
  private eventHandlers: Map<string, Function[]> = new Map();
  private sessionKey = 'chatbot-session';
  private sessionExpiry = 60 * 60 * 1000; // 1 hour for guests

  constructor(socketUrl: string) {
    this.socketUrl = socketUrl;
  }

  // Event handling
  on(event: string, handler: Function) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  off(event: string, handler?: Function) {
    if (!this.eventHandlers.has(event)) return;
    
    if (!handler) {
      this.eventHandlers.delete(event);
    } else {
      const handlers = this.eventHandlers.get(event)!;
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, ...args: any[]) {
    const handlers = this.eventHandlers.get(event) || [];
    handlers.forEach(handler => handler(...args));
  }

  // Session management
  private getStoredSession(): { sessionId: string; chatId: string; timestamp: number; email?: string } | null {
    try {
      const stored = localStorage.getItem(this.sessionKey);
      if (!stored) return null;
      
      const session = JSON.parse(stored);
      
      // Check if session has expired (only for guests)
      if (!session.email && Date.now() - session.timestamp > this.sessionExpiry) {
        this.clearStoredSession();
        return null;
      }
      
      return session;
    } catch (error) {
      this.clearStoredSession();
      return null;
    }
  }

  private storeSession(sessionId: string, chatId: string, email?: string) {
    try {
      const sessionData = {
        sessionId,
        chatId,
        timestamp: Date.now(),
        ...(email && { email })
      };
      localStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
    } catch (error) {
      // Handle localStorage errors silently
    }
  }

  private clearStoredSession() {
    try {
      localStorage.removeItem(this.sessionKey);
    } catch (error) {
      // Handle localStorage errors silently
    }
  }

  // Session validation
  private async validateSession(sessionId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.socketUrl}/session/${sessionId}`);
      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      return false;
    }
  }

  // Initialize session and connection
  async initialize(userInfo?: { firstname?: string; lastname?: string; email?: string }, options?: { 
    companyName?: string; 
    visitedPaths?: string[]; 
    currentPath?: string;
    showTypingIndicator?: boolean;
  }): Promise<{ success: boolean; sessionId?: string; chatId?: string; reconnected?: boolean; isReconnection?: boolean; chatHistory?: any[] }> {
    
    const storedSession = this.getStoredSession();
    let sessionId: string | null = null;
    let chatId: string | null = null;

    if (storedSession) {
      const isValid = await this.validateSession(storedSession.sessionId);
      if (isValid) {
        sessionId = storedSession.sessionId;
        chatId = storedSession.chatId;
      } else {
        this.clearStoredSession();
      }
    }

    // Connect to socket
    this.socket = io(this.socketUrl);

    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Failed to create socket connection'));
        return;
      }

      this.socket.on('connect', () => {
        this.emit('connect');
        
        // Join with session info
        const joinData: any = {
          name: userInfo?.firstname && userInfo?.lastname 
            ? `${userInfo.firstname} ${userInfo.lastname}` 
            : "Anonymous Guest",
          email: userInfo?.email || null,
          firstname: userInfo?.firstname || null,
          lastname: userInfo?.lastname || null,
          origin: options?.companyName,
          visitedPaths: options?.visitedPaths || [],
          currentPath: options?.currentPath || window.location.pathname,
          ...(sessionId && { sessionId }),
          ...(chatId && { chatId })
        };

        this.socket?.emit("guest:join", joinData);
      });

      this.socket.on('disconnect', () => {
        this.emit('disconnect');
      });

      this.socket.on("guest:joined", (data: { 
        sessionId: string; 
        chatId: string; 
        reconnected?: boolean; 
        isReconnection?: boolean;
        chatHistory?: any[];
      }) => {
        this.sessionId = data.sessionId;
        this.chatId = data.chatId;
        this.isInitialized = true;
        
        // Store session
        this.storeSession(data.sessionId, data.chatId, userInfo?.email);
        
        // Set up message handlers
        this.setupMessageHandlers(options);
        
        // Handle chat history if provided
        if (data.isReconnection && data.chatHistory && data.chatHistory.length > 0) {
          this.emit('chatHistoryRestored', data.chatHistory);
        } else if (data.isReconnection) {
          // Reconnection but no chat history - emit empty history event
          // console.log('🔄 Reconnection detected but no chat history found');
          this.emit('chatHistoryRestored', []);
        }
        
        resolve({
          success: true,
          sessionId: data.sessionId,
          chatId: data.chatId,
          reconnected: data.reconnected || !!sessionId,
          isReconnection: data.isReconnection,
          chatHistory: data.chatHistory
        });
      });

      this.socket.on('error', (error: any) => {
        this.emit('error', error);
        reject(error);
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        if (!this.isInitialized) {
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  private setupMessageHandlers(options?: { showTypingIndicator?: boolean }) {
    if (!this.socket) return;

    // Message handling
    this.socket.on("message", (data: any) => {
      this.emit('message', data);
    });

    // Message sent confirmation
    this.socket.on("message:sent", (data: any) => {
      this.emit('messageSent', data);
    });

    // Typing indicators
    if (options?.showTypingIndicator) {
      this.socket.on("admin:startTyping", (data: any) => {
        this.emit('adminStartTyping', data);
      });

      this.socket.on("admin:stopTyping", (data: any) => {
        this.emit('adminStopTyping', data);
      });

      this.socket.on("typing", (data: any) => {
        this.emit('typing', data);
      });
    }

    // Message seen events
    this.socket.on("messages:seenByAdmin", (data: any) => {
      this.emit('messagesSeenByAdmin', data);
    });

    this.socket.on("messages:marked", (data: any) => {
      this.emit('messagesMarked', data);
    });

    // Chat history events
    this.socket.on("guest:chatHistory", (data: { messages: any[] }) => {
      this.emit('chatHistory', data.messages);
    });
  }

  // Send message
  sendMessage(text: string, userInfo?: { firstname?: string; lastname?: string; email?: string }, options?: {
    companyName?: string;
    visitedPaths?: string[];
    currentPath?: string;
  }) {
    if (!this.socket || !this.isInitialized) {
      throw new Error('Session not initialized');
    }

    const messageData: any = {
      text,
      sender: "user",
      origin: options?.companyName,
      timestamp: new Date().toISOString(),
      paths: options?.visitedPaths || [],
      currentPath: options?.currentPath || window.location.pathname,
    };

    // Add user info if available
    if (userInfo?.firstname && userInfo?.lastname && userInfo?.email) {
      messageData.firstname = userInfo.firstname;
      messageData.lastname = userInfo.lastname;
      messageData.email = userInfo.email;
    }

    this.socket.emit("guest:message", messageData);
  }

  // Typing indicators
  startTyping() {
    if (this.socket && this.isInitialized) {
      this.socket.emit('guest:startTyping');
    }
  }

  stopTyping() {
    if (this.socket && this.isInitialized) {
      this.socket.emit('guest:stopTyping');
    }
  }

  // Mark messages as seen
  markMessagesSeen(messageIds: string[]) {
    if (this.socket && this.isInitialized) {
      this.socket.emit("guest:markSeen", { messageIds });
    }
  }

  // Request chat history
  getChatHistory() {
    if (this.socket && this.isInitialized) {
      this.socket.emit("guest:getChatHistory");
    }
  }

  // Send path navigation update
  updateNavigationPath(currentPath: string, visitedPaths: string[], userInfo?: { firstname?: string; lastname?: string; email?: string }) {
    if (this.socket && this.isInitialized) {
      const pathData: any = {
        currentPath,
        visitedPaths,
        timestamp: new Date().toISOString(),
      };

      // Add user info if available
      if (userInfo?.firstname && userInfo?.lastname && userInfo?.email) {
        pathData.firstname = userInfo.firstname;
        pathData.lastname = userInfo.lastname;
        pathData.email = userInfo.email;
      }

      this.socket.emit("guest:pathUpdate", pathData);
    }
  }

  // Connection status
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  get currentSessionId(): string | null {
    return this.sessionId;
  }

  get currentChatId(): string | null {
    return this.chatId;
  }

  // Cleanup
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isInitialized = false;
    this.sessionId = null;
    this.chatId = null;
    this.eventHandlers.clear();
  }
}

// const socketUrl = "http://localhost:5001";
const socketUrl = "https://chat-bot.gentauronline.com";
const fallBackPrimaryColor = "#003299";
const fallBackSecondaryColor = "#efb100";
const audioUrl = "https://cdn.gentaur.com/chat-bots/Voicy_Telegram%20SFX%205.mp3";

// Geist font family with fallbacks
const GEIST_FONT_FAMILY = '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Function to load Geist font from Google Fonts
const loadGeistFont = () => {
  // Check if font is already loaded
  if (document.querySelector('link[href*="fonts.googleapis.com"][href*="Geist"]')) {
    return;
  }

  // Create preconnect links for better performance
  const preconnect1 = document.createElement('link');
  preconnect1.rel = 'preconnect';
  preconnect1.href = 'https://fonts.googleapis.com';
  
  const preconnect2 = document.createElement('link');
  preconnect2.rel = 'preconnect';
  preconnect2.href = 'https://fonts.gstatic.com';
  preconnect2.crossOrigin = 'anonymous';

  // Create link element for Geist font
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&display=swap';
  
  // Add to document head
  document.head.appendChild(preconnect1);
  document.head.appendChild(preconnect2);
  document.head.appendChild(fontLink);
};

// Function to load custom animations
const loadCustomAnimations = () => {
  // Check if animations are already loaded
  if (document.querySelector('#chatbot-animations')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'chatbot-animations';
  style.textContent = `
    @keyframes slideIn {
      0% {
        width: 0;
        opacity: 0;
      }
      100% {
        width: 100%;
        opacity: 1;
      }
    }
    
    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
      }
      40% {
        transform: translateY(-6px);
      }
      60% {
        transform: translateY(-3px);
      }
    }
    
    @keyframes pulse {
      0% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.05);
        opacity: 0.9;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }
  `;
  
  document.head.appendChild(style);
};

// Function to darken a hex color
function darkenColor(hex: string, percent: number = 20): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Darken by reducing RGB values
  const darkenedR = Math.max(0, Math.floor(r * (100 - percent) / 100));
  const darkenedG = Math.max(0, Math.floor(g * (100 - percent) / 100));
  const darkenedB = Math.max(0, Math.floor(b * (100 - percent) / 100));
  
  // Convert back to hex
  return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
}

function formatTimeToHHMM() {
  const date = new Date();
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes} UTC`;
}

function formatChatHistoryMessages(chatHistory: any[], botAvatar?: string, userAvatar?: string): Message[] {
  return chatHistory.map((msg) => ({
    id: msg._id || "msg-" + Date.now() + Math.random(),
    text: msg.text,
    sender: (msg.sender === "admin" || msg.sender === "system" || msg.sender === "bot") ? "bot" : "user",
    timestamp: new Date(msg.timestamp),
    avatar: (msg.sender === "admin" || msg.sender === "system" || msg.sender === "bot") ? botAvatar : userAvatar,
    seenByAdmin: msg.seenByAdmin || false,
    seenByGuest: msg.seenByGuest || false,
    seenAt: msg.seenAt ? new Date(msg.seenAt) : undefined,
    originalSender: msg.sender,
    senderName: msg.senderName,
  }));
}

export const ChatBot: React.FC<ChatbotProps> = ({
  UID,
  config = {},
  onMessageSent,
  onMessageReceived,
  onConnect,
  onDisconnect,
  className,
}) => {
  const [serverConfig, setServerConfig] = useState<ChatbotConfig>({});
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  
  const mergedConfig = useMemo(() => {
    return { ...serverConfig, ...config };
  }, [serverConfig, config]);




  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"home" | "chat">("home");
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isAdminTyping, setIsAdminTyping] = useState(false);

  const [chatSessionManager, setChatSessionManager] = useState<ChatSessionManager | null>(null);
  const [unseenBotMessageCount, setUnseenBotMessageCount] = useState(0);
  const [sessionInfo, setSessionInfo] = useState<{ sessionId?: string; chatId?: string; reconnected?: boolean; isReconnection?: boolean; chatHistory?: any[] }>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  

  const counterRef = useRef<NodeJS.Timeout | null>(null);
  const [isHoveringChatButton, setIsHoveringChatButton] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Typing system refs and state
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCurrentlyTyping = useRef(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  
  // Seen system refs and state
  const unseenAdminMessages = useRef<Set<string>>(new Set());
  const markSeenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const pendingPathUpdates = useRef<{path: string, paths: string[]}[]>([]);
  const lastSentPath = useRef<string>("");
  const pathUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  



  // Track all visited paths with localStorage persistence and session timeout
  const [visitedPaths, setVisitedPaths] = useState<string[]>([]);
  const [pathsInitialized, setPathsInitialized] = useState(false);

  const updateVisitedPaths = (newPath: string) => {
    setVisitedPaths(prev => {
      let updated = prev;

      if (!prev.includes(newPath)) {
        updated = [...prev, newPath];
        try {
          localStorage.setItem('chatbot-visited-paths', JSON.stringify(updated));
          localStorage.setItem('chatbot-paths-timestamp', Date.now().toString());
        } catch (error) {
          // Handle localStorage errors silently
        }
      } else {
        try {
          localStorage.setItem('chatbot-paths-timestamp', Date.now().toString());
        } catch (error) {
          // Handle localStorage errors silently
        }
      }

      debouncedPathUpdate(newPath, updated);

      return updated;
    });
  };

  const clearVisitedPaths = () => {
    setVisitedPaths([]);
    try {
      localStorage.removeItem('chatbot-visited-paths');
      localStorage.removeItem('chatbot-paths-timestamp');
    } catch (error) {
      // Handle localStorage errors silently
    }
  };
  
  // Add current path on mount and track path changes
  useEffect(() => {
    // Wait for paths to be initialized from localStorage
    if (!pathsInitialized) {
      return;
    }
    
    const currentPath = window.location.pathname;
    
    // Add current path if not already in the list
    updateVisitedPaths(currentPath);
    
    const handlePopState = () => {
      const newPath = window.location.pathname;
      updateVisitedPaths(newPath);
    };
    
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      const newPath = window.location.pathname;
      updateVisitedPaths(newPath);
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      const newPath = window.location.pathname;
      updateVisitedPaths(newPath);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [pathsInitialized]);



   
  const isOpenRef = useRef(isOpen);
  const activeTabRef = useRef(activeTab);
  const chatSessionManagerRef = useRef(chatSessionManager);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  
  useEffect(() => {
    chatSessionManagerRef.current = chatSessionManager;
    
    if (chatSessionManager && chatSessionManager.isConnected) {
      processPendingPathUpdates();
    }
  }, [chatSessionManager]);

  useEffect(() => {
    if (isConnected && chatSessionManager) {
      processPendingPathUpdates();
    }
  }, [isConnected, chatSessionManager]);

  useEffect(() => {
    return () => {
      if (pathUpdateTimeoutRef.current) {
        clearTimeout(pathUpdateTimeoutRef.current);
      }
    };
  }, []);
  

  // Function to fetch bot config from server
  const fetchBotConfig = async () => {
    if (!UID) {
      setIsConfigLoading(false);
      return;
    }

    try {
      const response = await fetch(`${socketUrl}/bot-config/${UID}`);
      if (response.ok) {
        const botConfig = await response.json();
        const mappedConfig: ChatbotConfig = {
          companyName: botConfig.companyName,
          botName: botConfig.botName,
          homeScreenMessage: botConfig.homeScreenMessage,
          presentationMessage: botConfig.presentationMessage,
          botAvatar: botConfig.botAvatar,
          userAvatar: botConfig.userAvatar,
          theme: botConfig.theme,
          primaryColor: botConfig.primaryColor,
          secondaryColor: botConfig.secondaryColor,
          placeholder: botConfig.placeholder,
          welcomeMessage: botConfig.welcomeMessage,
          socketUrl: socketUrl,
          autoConnect: botConfig.autoConnect,
          showTypingIndicator: botConfig.showTypingIndicator,
          height: botConfig.height,
          width: botConfig.width,
          position: botConfig.position,
          firstname: botConfig.firstname,
          lastname: botConfig.lastname,
          email: botConfig.email,
          audioUrl: botConfig.audioUrl,
          enableNotificationSound: botConfig.enableNotificationSound,
        };
        setServerConfig(mappedConfig);
      }
    } catch (error) {
      // Error fetching bot config
    } finally {
      setIsConfigLoading(false);
    }
  };

  useEffect(() => {
    fetchBotConfig();
  }, [UID]);

  // Load Geist font and custom animations when component mounts
  useEffect(() => {
    loadGeistFont();
    loadCustomAnimations();
  }, []);

  // Initialize visited paths from localStorage after config is loaded
  useEffect(() => {
    if (isConfigLoading) return;
    
    // Check localStorage for existing paths and validate session timeout
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('chatbot-visited-paths');
        const lastUpdate = localStorage.getItem('chatbot-paths-timestamp');
        
        if (saved && lastUpdate) {
          const sessionTimeout = mergedConfig.sessionTimeoutHours ? 
            mergedConfig.sessionTimeoutHours * 60 * 60 * 1000 : 
            2 * 60 * 60 * 1000; // Default: 2 hours in milliseconds
          const timeSinceLastUpdate = Date.now() - parseInt(lastUpdate);
          
          // If session is still valid, restore saved paths
          if (timeSinceLastUpdate < sessionTimeout) {
            const parsedPaths = JSON.parse(saved);
            setVisitedPaths(parsedPaths);
          } else {
            // Session expired, clear old data
            localStorage.removeItem('chatbot-visited-paths');
            localStorage.removeItem('chatbot-paths-timestamp');
          }
        }
      } catch (error) {
        // Clear corrupted data
        localStorage.removeItem('chatbot-visited-paths');
        localStorage.removeItem('chatbot-paths-timestamp');
      }
    }
    
    // Mark paths as initialized
    setPathsInitialized(true);
  }, [isConfigLoading, mergedConfig.sessionTimeoutHours]);

  useEffect(() => {
    if (pathsInitialized && typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      immediatePathUpdate(currentPath, visitedPaths);
    }
  }, [pathsInitialized, visitedPaths]);

  // Aggressive fallback check for welcome message
  useEffect(() => {
    if (isConfigLoading || !mergedConfig.welcomeMessage) return;
    
    const fallbackTimer = setTimeout(() => {
      setMessages(currentMessages => {
        const hasRealMessages = currentMessages.some(msg => !msg.id.startsWith("welcome-"));
        const hasWelcomeMessage = currentMessages.some(msg => msg.id.startsWith("welcome-"));
        
        if (!hasRealMessages && !hasWelcomeMessage) {
          const welcomeMsg: Message = {
            id: "welcome-" + Date.now(),
            text: mergedConfig.welcomeMessage!,
            sender: "bot",
            timestamp: new Date(),
            avatar: mergedConfig.botAvatar,
          };
          
          return [welcomeMsg];
        }
        
        return currentMessages;
      });
    }, 2000);

    return () => clearTimeout(fallbackTimer);
  }, [isConfigLoading, mergedConfig.welcomeMessage, mergedConfig.botAvatar, chatSessionManager, isConnected]);

  // Specific check for session establishment without chat history
  useEffect(() => {
    if (!chatSessionManager || !isConnected || isConfigLoading || !mergedConfig.welcomeMessage) return;
    
    const sessionEstablishedTimer = setTimeout(() => {
      setMessages(currentMessages => {
        const hasRealMessages = currentMessages.some(msg => !msg.id.startsWith("welcome-"));
        const hasWelcomeMessage = currentMessages.some(msg => msg.id.startsWith("welcome-"));
        
        if (!hasRealMessages && !hasWelcomeMessage) {
          const welcomeMsg: Message = {
            id: "welcome-" + Date.now(),
            text: mergedConfig.welcomeMessage!,
            sender: "bot",
            timestamp: new Date(),
            avatar: mergedConfig.botAvatar,
          };
          
          return [welcomeMsg];
        }
        
        return currentMessages;
      });
    }, 500);

    return () => clearTimeout(sessionEstablishedTimer);
  }, [chatSessionManager, isConnected, isConfigLoading, mergedConfig.welcomeMessage, sessionInfo]);

  useEffect(() => {
    if (isConfigLoading) return;

    if (mergedConfig.autoConnect && !chatSessionManager) {
      initializeChatSession();
    }

    return () => {
      if (chatSessionManager) {
        chatSessionManager.disconnect();
      }
    };
  }, [isConfigLoading, mergedConfig.autoConnect, chatSessionManager]);

  useEffect(() => {
    if (isConfigLoading || !mergedConfig.welcomeMessage) {
      return;
    }

    const timers = [
      setTimeout(() => {
        setMessages(currentMessages => {
          const welcomeExists = currentMessages.some(msg => msg.id.startsWith("welcome-"));
          const hasRealMessages = currentMessages.some(msg => !msg.id.startsWith("welcome-"));
          
          if (!welcomeExists && !hasRealMessages) {
            const welcomeMsg: Message = {
              id: "welcome-" + Date.now(),
              text: mergedConfig.welcomeMessage!,
              sender: "bot",
              timestamp: new Date(),
              avatar: mergedConfig.botAvatar,
            };
            
            return [welcomeMsg];
          }
          
          return currentMessages;
        });
      }, 200),

      setTimeout(() => {
        setMessages(currentMessages => {
          const welcomeExists = currentMessages.some(msg => msg.id.startsWith("welcome-"));
          const hasRealMessages = currentMessages.some(msg => !msg.id.startsWith("welcome-"));
          
          if (!welcomeExists && !hasRealMessages) {
            const welcomeMsg: Message = {
              id: "welcome-" + Date.now(),
              text: mergedConfig.welcomeMessage!,
              sender: "bot",
              timestamp: new Date(),
              avatar: mergedConfig.botAvatar,
            };
            
            return [welcomeMsg];
          }
          
          return currentMessages;
        });
      }, 800)
    ];

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [isConfigLoading, mergedConfig.welcomeMessage, mergedConfig.botAvatar, chatSessionManager, isConnected]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);




  useEffect(() => {
    if (isOpen && activeTab === "chat") {
      setUnseenBotMessageCount(0);
      setTimeout(() => {
        markUnseenBotMessagesAsSeen();
      }, 100);
    }
  }, [isOpen, activeTab, messages, chatSessionManager]);

  const scheduleMarkAsSeen = () => {
    if (markSeenTimeoutRef.current) {
      clearTimeout(markSeenTimeoutRef.current);
    }
    
    markSeenTimeoutRef.current = setTimeout(() => {
      markMessagesAsSeen();
    }, 2000);
  };

  const markMessagesAsSeen = () => {
    if (unseenAdminMessages.current.size === 0) {
      return;
    }

    const messageIds = Array.from(unseenAdminMessages.current);
    
    const currentSessionManager = chatSessionManagerRef.current;
    if (currentSessionManager && currentSessionManager.isConnected) {
      currentSessionManager.markMessagesSeen(messageIds);
    }
    
    unseenAdminMessages.current.clear();
  };

  const initializeChatSession = async () => {
    if (!mergedConfig.socketUrl) {
      return;
    }

    // Create new session manager
    const sessionManager = new ChatSessionManager(mergedConfig.socketUrl);

    // Set up event handlers
    sessionManager.on('connect', () => {
      setIsConnected(true);
      onConnect?.();
    });

    sessionManager.on('disconnect', () => {
      setIsConnected(false);
      setIsAdminTyping(false);
      onDisconnect?.();
    });

    // Handle incoming messages
    sessionManager.on('message', (data: { text: string; sender: string; timestamp: string; _id?: string; seenByAdmin?: boolean; seenByGuest?: boolean; seenAt?: string; senderName?: string }) => {
      const newMessage: Message = {
        id: data._id || "msg-" + Date.now() + Math.random(),
        text: data.text,
        sender: (data.sender === "bot" || data.sender === "system" || data.sender === "admin") ? "bot" : "user",
        timestamp: new Date(data.timestamp),
        avatar:
          (data.sender === "bot" || data.sender === "system" || data.sender === "admin")
            ? mergedConfig.botAvatar
            : mergedConfig.userAvatar,
        seenByAdmin: data.seenByAdmin || false,
        seenByGuest: data.seenByGuest || false,
        seenAt: data.seenAt ? new Date(data.seenAt) : undefined,
        // Preserve original sender info to distinguish admin from bot messages
        originalSender: data.sender,
        senderName: data.senderName,
      };

            setMessages((prev) => [...prev, newMessage]);
      
              // Play sound for bot/system/admin messages
        if (data.sender === "bot" || data.sender === "system" || data.sender === "admin") {
          // Play sound if enabled in config (default is true)
          if (mergedConfig.enableNotificationSound !== false) {
            try {
              const soundUrl = mergedConfig.audioUrl || audioUrl;
              const audio = new Audio(soundUrl);
              audio.play().catch(e => {
                // Silently handle audio play failures in production
                if (process.env.NODE_ENV === 'development') {
                  console.log("Audio play failed:", e);
                }
              });
            } catch (error) {
              // Silently handle audio creation failures in production
              if (process.env.NODE_ENV === 'development') {
                console.log("Audio creation failed:", error);
              }
            }
          }
          
          // Increment unseen count if chat is not open or not on chat tab
          if (!isOpenRef.current || activeTabRef.current !== "chat") {
            setUnseenBotMessageCount(prev => prev + 1);
          }
        }

        // console.log("new message", newMessage);

      onMessageReceived?.(newMessage);
      
      if (newMessage.sender === "bot" && data._id) {
        unseenAdminMessages.current.add(data._id);
        
        if (isOpenRef.current && activeTabRef.current === "chat") {
          setTimeout(() => {
            if (chatSessionManagerRef.current && chatSessionManagerRef.current.isConnected && data._id) {
              chatSessionManagerRef.current.markMessagesSeen([data._id]);
            }
          }, 100);
        }
      }
      
      // Clear admin typing when message is received
      setIsAdminTyping(false);
    });

    sessionManager.on('messageSent', (data: { _id?: string; text?: string }) => {
      if (data._id && data.text) {
        setMessages(prev => prev.map(msg => {
          const isRecentMessage = msg.sender === "user" && 
            msg.text === data.text &&
            (Date.now() - msg.timestamp.getTime()) < 10000;
          
          if (isRecentMessage && data._id) {
            return { ...msg, id: data._id };
          }
          return msg;
        }));
      }
    });

    // Handle typing indicators
    sessionManager.on('adminStartTyping', (data: { adminName: string; chatId: string }) => {
      if (mergedConfig.showTypingIndicator) {
        setIsAdminTyping(true);
      }
    });

    sessionManager.on('adminStopTyping', (data: { adminName: string; chatId: string }) => {
      if (mergedConfig.showTypingIndicator) {
        setIsAdminTyping(false);
      }
    });

    sessionManager.on('typing', (data: { isTyping: boolean }) => {
      if (mergedConfig.showTypingIndicator) {
        setIsAdminTyping(data.isTyping);
      }
    });

    sessionManager.on('messagesSeenByAdmin', (data: { adminName: string; seenCount: number; messageIds: string[] }) => {
      setMessages(prev => {
        const updatedMessages = prev.map(msg => {
          const isMessageSeen = data.messageIds.includes(msg.id);
          
          return isMessageSeen 
            ? { ...msg, seenByAdmin: true, seenAt: new Date() }
            : msg;
        });
        
        return updatedMessages;
      });
    });

    sessionManager.on('chatHistoryRestored', (chatHistory: any[]) => {
      const restoredMessages = formatChatHistoryMessages(chatHistory, mergedConfig.botAvatar, mergedConfig.userAvatar);
      const welcomeMessage: Message = {
        "id": "welcome-" + Date.now(),
        "text": mergedConfig.welcomeMessage!,
        "sender": "bot",
        "timestamp": new Date(),
        "avatar": mergedConfig.botAvatar,
        "seenByAdmin": false,
        "seenByGuest": false,
        "originalSender": "system",
        "senderName": "System"
      }
      
 
      

      const unseenCount = chatHistory.filter(msg => 
         (msg.sender === "bot" || msg.sender === "system" || msg.sender === "admin") && 
        !msg.seenByGuest && msg.text !== "New user connected to chat" && msg.text !== "Oops ! Agents are busy. Leave your email, and we’ll be in touch 😊!"
      ).length;
      
      if (!isOpenRef.current || activeTabRef.current !== "chat") {
        setUnseenBotMessageCount(unseenCount);
      }
      
      setMessages([welcomeMessage, ...restoredMessages]);
      
      if (restoredMessages.length === 0 && mergedConfig.welcomeMessage) {
        setTimeout(() => {
          setMessages(currentMessages => {
            const hasRealMessages = currentMessages.some(msg => !msg.id.startsWith("welcome-"));
            const hasWelcome = currentMessages.some(msg => msg.id.startsWith("welcome-"));
            
            if (!hasRealMessages && !hasWelcome) {
              const welcomeMsg: Message = {
                id: "welcome-" + Date.now(),
                text: mergedConfig.welcomeMessage!,
                sender: "bot",
                timestamp: new Date(),
                avatar: mergedConfig.botAvatar,
              };
              return [welcomeMsg];
            }
            return currentMessages;
          });
        }, 200);
      }
    });

    sessionManager.on('chatHistory', (chatHistory: any[]) => {
      const historyMessages = formatChatHistoryMessages(chatHistory, mergedConfig.botAvatar, mergedConfig.userAvatar);
      setMessages(historyMessages);
      
      if (historyMessages.length === 0 && mergedConfig.welcomeMessage) {
        setTimeout(() => {
          setMessages(currentMessages => {
            const hasWelcome = currentMessages.some(msg => msg.id.startsWith("welcome-"));
            if (!hasWelcome) {
              const welcomeMsg: Message = {
                id: "welcome-" + Date.now(),
                text: mergedConfig.welcomeMessage!,
                sender: "bot",
                timestamp: new Date(),
                avatar: mergedConfig.botAvatar,
              };
              return [welcomeMsg];
            }
            return currentMessages;
          });
        }, 100);
      }
    });

    try {
      // Initialize session with user info and options
      const userInfo = {
        firstname: mergedConfig.firstname,
        lastname: mergedConfig.lastname,
        email: mergedConfig.email,
      };

      const options = {
        companyName: mergedConfig.companyName,
        visitedPaths: visitedPaths,
        currentPath: window.location.pathname,
        showTypingIndicator: mergedConfig.showTypingIndicator,
      };

      const result = await sessionManager.initialize(userInfo, options);
      
      if (result.success) {
        setChatSessionManager(sessionManager);
        setSessionInfo(result);
        
        if (result.isReconnection && result.chatHistory && result.chatHistory.length > 0) {
          // Chat history will be restored via the event handler
        }
      }
    } catch (error) {
      console.error('Failed to initialize chat session:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = () => {
    if (!inputText.trim() || !chatSessionManager) return;

    const userMessage: Message = {
      id: "user-" + Date.now(),
      text: inputText,
      sender: "user",
      timestamp: new Date(),
      avatar: mergedConfig.userAvatar,
    };

    setMessages((prev) => [...prev, userMessage]);
    setHasUserSentMessage(true);

    stopTyping();

    const isLoggedIn = mergedConfig.firstname && 
                      mergedConfig.lastname && 
                      mergedConfig.email && 
                      mergedConfig.firstname !== "" && 
                      mergedConfig.lastname !== "" && 
                      mergedConfig.email !== "";

    const userInfo = isLoggedIn ? {
      firstname: mergedConfig.firstname!,
      lastname: mergedConfig.lastname!,
      email: mergedConfig.email!,
    } : undefined;

    const options = {
      companyName: mergedConfig.companyName,
      visitedPaths: visitedPaths,
      currentPath: window.location.pathname,
    };
    
    chatSessionManager.sendMessage(inputText, userInfo, options);

    onMessageSent?.(inputText);
    setInputText("");
  };

  // Utility function to manually request chat history
  const requestChatHistory = () => {
    if (chatSessionManager) {
      chatSessionManager.getChatHistory();
    }
  };

  // Path tracking utility functions
  const sendPathUpdate = (currentPath: string, visitedPaths: string[]) => {
    if (!chatSessionManager || !chatSessionManager.isConnected) {
      pendingPathUpdates.current.push({ path: currentPath, paths: [...visitedPaths] });
      return;
    }

    const isLoggedIn = mergedConfig.firstname && 
                      mergedConfig.lastname && 
                      mergedConfig.email && 
                      mergedConfig.firstname !== "" && 
                      mergedConfig.lastname !== "" && 
                      mergedConfig.email !== "";

    const userInfo = isLoggedIn ? {
      firstname: mergedConfig.firstname!,
      lastname: mergedConfig.lastname!,
      email: mergedConfig.email!,
    } : undefined;

    chatSessionManager.updateNavigationPath(currentPath, visitedPaths, userInfo);
    lastSentPath.current = currentPath;
  };

  const processPendingPathUpdates = () => {
    if (pendingPathUpdates.current.length === 0 || !chatSessionManager || !chatSessionManager.isConnected) {
      return;
    }

    const updates = pendingPathUpdates.current;
    const latestUpdate = updates[updates.length - 1];
    
    if (latestUpdate) {
      sendPathUpdate(latestUpdate.path, latestUpdate.paths);
    }
    
    pendingPathUpdates.current = [];
  };

  const debouncedPathUpdate = (currentPath: string, visitedPaths: string[]) => {
    if (pathUpdateTimeoutRef.current) {
      clearTimeout(pathUpdateTimeoutRef.current);
    }

    pathUpdateTimeoutRef.current = setTimeout(() => {
      sendPathUpdate(currentPath, visitedPaths);
    }, 100);
  };

  const immediatePathUpdate = (currentPath: string, visitedPaths: string[]) => {
    sendPathUpdate(currentPath, visitedPaths);
  };

  const startTyping = () => {
    if (!chatSessionManager || !isConnected || !hasUserSentMessage) return;
    
    if (!isCurrentlyTyping.current) {
      isCurrentlyTyping.current = true;
      chatSessionManager.startTyping();
    }
  };

  const stopTyping = () => {
    if (!chatSessionManager || !isConnected) return;
    
    if (isCurrentlyTyping.current) {
      isCurrentlyTyping.current = false;
      chatSessionManager.stopTyping();
    }
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputText(value);
    
    if (!chatSessionManager || !isConnected) return;
    
    if (!hasUserSentMessage) return;
    
    if (value.trim()) {
      startTyping();
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping();
      }, 2000);
    } else {
      stopTyping();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Mark all unseen bot messages as seen
  const markUnseenBotMessagesAsSeen = () => {
    const unseenBotMessageIds = messages
      .filter(msg => 
        (msg.sender === "bot" || msg.originalSender === "admin") && 
        !msg.seenByGuest && 
        msg.id && 
        !msg.id.startsWith("welcome-")
      )
      .map(msg => msg.id);
    
    if (unseenBotMessageIds.length > 0 && chatSessionManager) {
      chatSessionManager.markMessagesSeen(unseenBotMessageIds);
    }
  };

  const handleChatNowClick = () => {
    setActiveTab("chat");
    setUnseenBotMessageCount(0);
    markUnseenBotMessagesAsSeen();
  };

  const toggleChat = () => {
    if (isAnimating) return;
    
    if (isOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsAnimating(false);
      }, 200);
    } else {
      setIsAnimating(true);
      setIsOpen(true);
      setUnseenBotMessageCount(0);
      setTimeout(() => {
        markUnseenBotMessagesAsSeen();
      }, 100);
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
      
      if (!chatSessionManager && mergedConfig.autoConnect) {
        initializeChatSession();
      }
    }
  };

  const getPositionStyles = () => {
    switch (mergedConfig.position) {
      case "bottom-left":
        return { bottom: '1rem', left: '1rem' };
      case "top-right":
        return { top: '1rem', right: '1rem' };
      case "top-left":
        return { top: '1rem', left: '1rem' };
      case "bottom-right":
        return { bottom: '1rem', right: '1rem' };
      default:
        return { bottom: '1rem', right: '1rem' };
    }
  };

  const renderHomeContent = () => (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      backgroundColor: `${mergedConfig.primaryColor || fallBackPrimaryColor}08`,
      padding: '1.5rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decoration */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-50%',
        width: '200%',
        height: '200%',
        background: `radial-gradient(circle, ${mergedConfig.primaryColor || fallBackPrimaryColor}08 0%, transparent 70%)`,
        pointerEvents: 'none'
      }} />
      
      <div style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2rem'
      }}>
        <Avatar style={{ 
          width: '6rem', 
          height: '6rem', 
          boxShadow: `0 20px 40px -12px ${mergedConfig.primaryColor || fallBackPrimaryColor}40, 0 8px 16px -8px ${mergedConfig.secondaryColor || fallBackSecondaryColor}30`,
          border: `3px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}20`,
          background: 'white'
        }}>
          <AvatarImage src={mergedConfig.botAvatar || ''} alt={`${mergedConfig.companyName} logo`} />
          <AvatarFallback style={{
            backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor,
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {mergedConfig.companyName
              ?.split(" ")
              .map((word: string) => word[0].toUpperCase())
              .join("")}
          </AvatarFallback>
        </Avatar>

        <Card style={{ 
          width: '100%', 
          maxWidth: '280px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 10px 20px -8px rgba(0, 0, 0, 0.1)', 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '1rem',
          overflow: 'hidden'
        }}>
          <CardContent style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ 
                width: '3rem', 
                height: '3rem', 
                minWidth: '3rem', 
                minHeight: '3rem', 
                borderRadius: '50%', 
                backgroundColor: `${mergedConfig.primaryColor || fallBackPrimaryColor}20`,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${mergedConfig.primaryColor || fallBackPrimaryColor}25`
              }}>
                <UserRound style={{ height: '1.5rem', width: '1.5rem', color: mergedConfig.primaryColor || fallBackPrimaryColor }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                <p style={{ 
                  fontSize: '0.8rem', 
                  color: '#64748b', 
                  fontFamily: GEIST_FONT_FAMILY,
                  fontWeight: '500',
                  letterSpacing: '0.025em'
                }}>
                  {mergedConfig.companyName} • {formatTimeToHHMM()}
                </p>
                <p style={{ 
                  fontSize: '0.95rem', 
                  color: '#1e293b', 
                  fontFamily: GEIST_FONT_FAMILY,
                  lineHeight: '1.5',
                  fontWeight: '400'
                }}>
                  {mergedConfig.presentationMessage}
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter style={{ padding: '1.5rem', paddingTop: '0' }}>
            <CardAction style={{ width: '100%' }}>
                              <Button
                size="sm"
                variant="outline"
                style={{
                  width: '100%',
                  height: '2.5rem',
                  background: isHoveringChatButton 
                    ? darkenColor(mergedConfig.primaryColor || fallBackPrimaryColor, 8)
                    : (mergedConfig.primaryColor || fallBackPrimaryColor),
                  border: 'none',
                  color: '#ffffff',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  borderRadius: '0.5rem',
                  boxShadow: `0 4px 14px 0 ${mergedConfig.primaryColor || fallBackPrimaryColor}30`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isHoveringChatButton ? 'translateY(-1px)' : 'translateY(0)',
                  fontFamily: GEIST_FONT_FAMILY
                }}
                onMouseEnter={() => setIsHoveringChatButton(true)}
                onMouseLeave={() => setIsHoveringChatButton(false)}
                onClick={handleChatNowClick}
              >
                <Send style={{ height: '1rem', width: '1rem', marginRight: '0.5rem' }} /> 
                Start Conversation
              </Button>
            </CardAction>
          </CardFooter>
        </Card>
      </div>
    </div>
  );

  const renderChatContent = () => (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      width: '100%', 
      backgroundColor: '#ffffff',
      position: 'relative',
      overflow: 'hidden'
    }}>
      
      {/* Header - Fixed */}
      <div style={{ 
        flexShrink: 0, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        paddingLeft: '1.5rem', 
        paddingRight: '1.5rem', 
        paddingTop: '1rem', 
        paddingBottom: '1rem', 
        backgroundColor: '#ffffff',
        borderBottom: `1px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}15`,
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Avatar style={{ 
            height: '2.5rem', 
            width: '2.5rem',
            boxShadow: `0 4px 12px ${mergedConfig.primaryColor || fallBackPrimaryColor}25`,
            border: `2px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}20`
          }}>
            <AvatarImage src={mergedConfig.botAvatar} />
            <AvatarFallback style={{ 
              backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor,
              color: 'white',
              fontWeight: '600'
            }}>
              {mergedConfig.companyName
                ?.split(" ")
                .map((word: string) => word[0].toUpperCase())
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 style={{ 
              fontWeight: '700', 
              fontFamily: GEIST_FONT_FAMILY,
              color: '#1e293b',
              fontSize: '1rem',
              margin: 0,
              marginBottom: '0.125rem'
            }}>
              {mergedConfig.botName}
            </h3>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.375rem', 
              fontSize: '0.8rem', 
              fontFamily: GEIST_FONT_FAMILY,
              color: '#64748b',
              fontWeight: '500'
            }}>
              <div
                style={{
                  width: '0.5rem',
                  height: '0.5rem',
                  borderRadius: '50%',
                  backgroundColor: isConnected ? '#10b981' : '#ef4444',
                  boxShadow: isConnected ? '0 0 6px #10b98140' : '0 0 6px #ef444440'
                }}
              />
              {isConnected ? "Online" : "Offline"}
            </div>
          </div>
        </div>
      </div>

      {/* Messages - Scrollable */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        backgroundColor: '#ffffff',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ 
          padding: '1.5rem', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '1rem',
          paddingBottom: '2rem',
          backgroundColor: '#ffffff'
        }}>
          {messages.filter((message) => {
            if (message.text.includes("New user connected to chat")) {
              return false;
            }
            if (message.id.startsWith("welcome-")) {
              return true;
            }
            if (message.originalSender === "admin") {
              return true;
            }
            if (message.originalSender === "system") {
              return true;
            }
            if (message.sender === "bot") {
              return false;
            }
            return true;
          }).map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
                justifyContent: message.sender === "user" ? 'flex-end' : 'flex-start',
                marginBottom: '0.5rem'
              }}
            >
              {message.sender === "bot" && (
                <Avatar style={{ 
                  height: '2.25rem', 
                  width: '2.25rem', 
                  flexShrink: 0,
                  boxShadow: `0 2px 8px ${mergedConfig.primaryColor || fallBackPrimaryColor}25`,
                  border: `2px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}15`
                }}>
                  <AvatarImage src={message.avatar} />
                  <AvatarFallback style={{
                    backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor,
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {mergedConfig.companyName
                      ?.split(" ")
                      .map((word: string) => word[0].toUpperCase())
                      .join("")}
                  </AvatarFallback>
                </Avatar>
              )}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: 'calc(100% - 3.5rem)',
                alignItems: message.sender === "user" ? 'flex-end' : 'flex-start'
              }}>
                <div
                  style={{
                    borderRadius: message.sender === "user" ? '1.25rem 1.25rem 0.5rem 1.25rem' : '1.25rem 1.25rem 1.25rem 0.5rem',
                    paddingLeft: '1.25rem',
                    paddingRight: '1.25rem',
                    paddingTop: '0.75rem',
                    paddingBottom: '0.75rem',
                    fontSize: '0.9rem',
                    wordBreak: 'break-word',
                    fontFamily: GEIST_FONT_FAMILY,
                    lineHeight: '1.5',
                    maxWidth: '280px',
                    position: 'relative',
                    ...(message.sender === "user" ? {
                      color: 'white',
                      backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor,
                      boxShadow: `0 4px 12px ${mergedConfig.primaryColor || fallBackPrimaryColor}25`,
                    } : {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      color: '#1e293b',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    })
                  }}
                >
                  {message.text}
                </div>
                {message.sender === "user" && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end', 
                    gap: '0.375rem', 
                    marginTop: '0.375rem' 
                  }}>
                    <span style={{ 
                      fontSize: '0.7rem', 
                      color: '#64748b', 
                      fontFamily: GEIST_FONT_FAMILY,
                      fontWeight: '500'
                    }}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {message.seenByAdmin ? (
                      <div style={{ display: 'flex', opacity: 0.8 }}>
                        <Check style={{ height: '0.75rem', width: '0.75rem', color: mergedConfig.primaryColor || fallBackPrimaryColor }} />
                        <Check style={{ height: '0.75rem', width: '0.75rem', color: mergedConfig.primaryColor || fallBackPrimaryColor, marginLeft: '-0.25rem' }} />
                      </div>
                    ) : (
                      <Check style={{ height: '0.75rem', width: '0.75rem', color: mergedConfig.primaryColor || fallBackPrimaryColor, opacity: 0.6 }} />
                    )}
                  </div>
                )}
                {message.sender === "bot" && (
                  <span style={{ 
                    fontSize: '0.7rem', 
                    color: '#64748b', 
                    marginTop: '0.375rem', 
                    fontFamily: GEIST_FONT_FAMILY,
                    fontWeight: '500'
                  }}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              {message.sender === "user" && (
                <Avatar style={{ 
                  height: '2.25rem', 
                  width: '2.25rem', 
                  flexShrink: 0,
                  boxShadow: `0 2px 8px ${mergedConfig.primaryColor || fallBackPrimaryColor}25`,
                  border: `2px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}15`
                }}>
                  <AvatarImage src={message.avatar} />
                  {mergedConfig.email && mergedConfig.firstname && mergedConfig.lastname ? (
                    <AvatarFallback style={{
                      backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor,
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {mergedConfig.firstname.charAt(0).toUpperCase() + mergedConfig.lastname.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  ) : (
                    <AvatarFallback style={{
                      backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor,
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      U
                    </AvatarFallback>
                  )}
                </Avatar>
              )}
            </div>
          ))}

          {isAdminTyping && mergedConfig.showTypingIndicator && (
            <div style={{ 
              display: 'flex', 
              gap: '0.75rem', 
              justifyContent: 'flex-start',
              marginBottom: '0.5rem'
            }}>
              <Avatar style={{ 
                height: '2.25rem', 
                width: '2.25rem', 
                flexShrink: 0,
                boxShadow: `0 2px 8px ${mergedConfig.primaryColor || fallBackPrimaryColor}25`,
                border: `2px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}15`
              }}>
                <AvatarImage src={mergedConfig.botAvatar} />
                <AvatarFallback style={{
                  backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor,
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {mergedConfig.companyName
                    ?.split(" ")
                    .map((word: string) => word[0].toUpperCase())
                    .join("")}
                </AvatarFallback>
              </Avatar>
              <div style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                borderRadius: '1.25rem 1.25rem 1.25rem 0.5rem', 
                padding: '1rem 1.25rem', 
                fontSize: '0.875rem', 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.25rem', 
                fontFamily: GEIST_FONT_FAMILY,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                minWidth: '60px'
              }}>
                <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'center' }}>
                  <div 
                    style={{ 
                      width: '0.5rem', 
                      height: '0.5rem', 
                      backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor, 
                      borderRadius: '50%',
                      animation: 'bounce 1s infinite',
                      opacity: 0.7
                    }} 
                  />
                  <div
                    style={{ 
                      width: '0.5rem', 
                      height: '0.5rem', 
                      backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor, 
                      borderRadius: '50%',
                      animation: 'bounce 1s infinite',
                      animationDelay: "0.1s",
                      opacity: 0.7
                    }}
                  />
                  <div
                    style={{ 
                      width: '0.5rem', 
                      height: '0.5rem', 
                      backgroundColor: mergedConfig.primaryColor || fallBackPrimaryColor, 
                      borderRadius: '50%',
                      animation: 'bounce 1s infinite',
                      animationDelay: "0.2s",
                      opacity: 0.7
                    }}
                  />
                </div>
              </div>
            </div>
          )}

      
          

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input - Fixed */}
      <div style={{ 
        flexShrink: 0, 
        padding: '1.5rem', 
        borderTop: `1px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}15`, 
        backgroundColor: '#ffffff',
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem',
          alignItems: 'flex-end',
          backgroundColor: '#f8f9fa',
          borderRadius: '1.5rem',
          padding: '0.75rem',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        }}>
          <input
            type="text"
            value={inputText}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={mergedConfig.placeholder || "Type your message..."}
            style={{ 
              flex: 1, 
              paddingLeft: '1rem', 
              paddingRight: '1rem', 
              paddingTop: '0.75rem', 
              paddingBottom: '0.75rem', 
              fontSize: '0.9rem', 
              border: 'none', 
              borderRadius: '1rem', 
              backgroundColor: 'transparent',
              color: '#1e293b',
              fontFamily: GEIST_FONT_FAMILY,
              outline: 'none',
              minHeight: '1.25rem',
              resize: 'none'
            }}
            onFocus={(e) => {
              e.target.parentElement!.style.borderColor = `${mergedConfig.primaryColor || fallBackPrimaryColor}40`;
              e.target.parentElement!.style.boxShadow = `0 4px 20px ${mergedConfig.primaryColor || fallBackPrimaryColor}20`;
              e.target.parentElement!.style.backgroundColor = '#ffffff';
            }}
            onBlur={(e) => {
              e.target.parentElement!.style.borderColor = 'rgba(0, 0, 0, 0.08)';
              e.target.parentElement!.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.target.parentElement!.style.backgroundColor = '#f8f9fa';
            }}
            disabled={!isConnected}
          />
          <Button
            onClick={sendMessage}
            disabled={!inputText.trim() || !isConnected}
            size="icon"
            style={{
              background: !inputText.trim() || !isConnected 
                ? '#e2e8f0' 
                : (mergedConfig.primaryColor || fallBackPrimaryColor),
              border: 'none',
              borderRadius: '50%',
              width: '2.5rem',
              height: '2.5rem',
              boxShadow: !inputText.trim() || !isConnected 
                ? 'none' 
                : `0 4px 12px ${mergedConfig.primaryColor || fallBackPrimaryColor}40`,
              transition: 'all 0.2s ease',
              transform: inputText.trim() && isConnected ? 'scale(1.05)' : 'scale(1)',
              cursor: !inputText.trim() || !isConnected ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (inputText.trim() && isConnected) {
                e.currentTarget.style.transform = 'scale(1.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (inputText.trim() && isConnected) {
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
          >
            <Send style={{ 
              height: '1.125rem', 
              width: '1.125rem',
              color: !inputText.trim() || !isConnected ? '#94a3b8' : 'white'
            }} />
          </Button>
        </div>
      </div>
    </div>
  );

  // Don't render until config is loaded
  if (isConfigLoading) {
    return null;
  }

      return (
    <div style={{
      position: 'fixed',
      zIndex: 50,
      fontFamily: GEIST_FONT_FAMILY,
      ...getPositionStyles()
    }}>
      {/* Chat Window */}
      {(isOpen || isAnimating) && (
        <div 
          style={{
            marginBottom: '1rem',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}20`,
            borderRadius: '1.5rem',
            boxShadow: `0 20px 40px -12px ${mergedConfig.primaryColor || fallBackPrimaryColor}25, 0 8px 16px -8px rgba(0, 0, 0, 0.1)`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transform: isOpen && !isAnimating ? 'translateY(0) scale(1)' : 'translateY(1rem) scale(0.95)',
            opacity: isOpen && !isAnimating ? 1 : 0,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: mergedConfig.width || '360px',
            height: activeTab === "home" 
              ? (mergedConfig.height ? `${parseInt(mergedConfig.height.toString()) * 0.8}px` : 'auto')
              : (mergedConfig.height || '640px')
          }}
        >
          {/* Tabs Navigation */}
          <div style={{ 
            display: 'flex', 
            backgroundColor: `${mergedConfig.primaryColor || fallBackPrimaryColor}05`,
            borderBottom: `1px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}15`,
            position: 'relative'
          }}>
            <button
              onClick={() => setActiveTab("home")}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                fontFamily: GEIST_FONT_FAMILY,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                border: 'none',
                background: activeTab === "home" 
                  ? `${mergedConfig.primaryColor || fallBackPrimaryColor}12`
                  : 'transparent',
                borderRadius: activeTab === "home" ? '0.75rem 0.75rem 0 0' : '0',
                cursor: 'pointer',
                color: activeTab === "home" ? (mergedConfig.primaryColor || fallBackPrimaryColor) : '#64748b',
                position: 'relative',
                transform: activeTab === "home" ? 'translateY(-1px)' : 'translateY(0)'
              }}
            >
              <Home style={{ 
                height: '1.125rem', 
                width: '1.125rem',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: activeTab === "home" ? 'scale(1.1)' : 'scale(1)'
              }} />
              Home
              {activeTab === "home" && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  width: '100%',
                  height: '3px',
                  background: (mergedConfig.primaryColor || fallBackPrimaryColor),
                  borderRadius: '2px',
                  animation: 'slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }} />
              )}
            </button>
            <button
              onClick={() => setActiveTab("chat")}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                fontFamily: GEIST_FONT_FAMILY,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                border: 'none',
                background: activeTab === "chat" 
                  ? `${mergedConfig.primaryColor || fallBackPrimaryColor}12`
                  : 'transparent',
                borderRadius: activeTab === "chat" ? '0.75rem 0.75rem 0 0' : '0',
                cursor: 'pointer',
                color: activeTab === "chat" ? (mergedConfig.primaryColor || fallBackPrimaryColor) : '#64748b',
                position: 'relative',
                transform: activeTab === "chat" ? 'translateY(-1px)' : 'translateY(0)'
              }}
            >
              <MessageCircle style={{ 
                height: '1.125rem', 
                width: '1.125rem',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: activeTab === "chat" ? 'scale(1.1)' : 'scale(1)'
              }} />
              Chat
              {activeTab === "chat" && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  width: '100%',
                  height: '3px',
                  background: (mergedConfig.primaryColor || fallBackPrimaryColor),
                  borderRadius: '2px',
                  animation: 'slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }} />
              )}
            </button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleChat}
              style={{ 
                margin: '0.75rem',
                height: '2.5rem',
                width: '2.5rem',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.9)',
                border: `1px solid ${mergedConfig.primaryColor || fallBackPrimaryColor}20`,
                color: mergedConfig.primaryColor || fallBackPrimaryColor,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${mergedConfig.primaryColor || fallBackPrimaryColor}10`;
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X style={{ height: '1.125rem', width: '1.125rem' }} />
            </Button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {activeTab === "home" ? renderHomeContent() : renderChatContent()}
          </div>
        </div>
      )}

      {/* Toggle Button */}
      {!isOpen && (
        <div style={{ position: 'relative' }}>
          <div
            onClick={toggleChat}
            style={{
              height: '4rem',
              width: '4rem',
              borderRadius: '50%',
              background: `${mergedConfig.primaryColor || fallBackPrimaryColor}`,
              boxShadow: `0 16px 32px -8px ${mergedConfig.primaryColor || fallBackPrimaryColor}40, 0 8px 16px -4px rgba(0, 0, 0, 0.1)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'scale(1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = `0 20px 40px -8px ${mergedConfig.primaryColor || fallBackPrimaryColor}50, 0 12px 20px -4px rgba(0, 0, 0, 0.15)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = `0 16px 32px -8px ${mergedConfig.primaryColor || fallBackPrimaryColor}40, 0 8px 16px -4px rgba(0, 0, 0, 0.1)`;
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
          >
            {/* Subtle shine effect */}
            <div style={{
              position: 'absolute',
              top: '10%',
              left: '10%',
              width: '30%',
              height: '30%',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '50%',
              filter: 'blur(8px)',
              pointerEvents: 'none'
            }} />
            <MessageSquareMore style={{ 
              height: '1.75rem', 
              width: '1.75rem', 
              color: 'white',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
              position: 'relative',
              zIndex: 1
            }} />
          </div>
          {/* Unseen message count badge */}
          {unseenBotMessageCount > 0 && (
            <div 
              style={{
                position: 'absolute',
                top: '-0.375rem',
                right: '-0.375rem',
                height: '1.75rem',
                width: '1.75rem',
                borderRadius: '50%',
                fontSize: '0.75rem',
                fontWeight: '700',
                fontFamily: GEIST_FONT_FAMILY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: mergedConfig.secondaryColor || fallBackSecondaryColor,
                color: 'white',
                border: '2px solid white',
                boxShadow: `0 4px 12px ${mergedConfig.secondaryColor || fallBackSecondaryColor}50, 0 2px 6px rgba(0, 0, 0, 0.1)`,
                animation: 'pulse 2s infinite'
              }}
            >
              {unseenBotMessageCount > 9 ? '9+' : unseenBotMessageCount}
            </div>
          )}
        </div>
      )}
    </div>
  );
  
};
